import { useEffect, useState, useRef, useCallback } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import aboutExperience from "@/assets/about-experience.png";
import aboutGlobal from "@/assets/about-global.png";
import aboutFintech from "@/assets/about-fintech.png";
import aboutProjects from "@/assets/about-projects.png";

interface MousePosition {
  x: number;
  y: number;
}

const AboutCompanySection = () => {
  const { t } = useLanguage();
  const [scrollY, setScrollY] = useState(0);
  const [mousePosition, setMousePosition] = useState<MousePosition>({ x: 0, y: 0 });
  const sectionRef = useRef<HTMLElement>(null);
  const cardsContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      if (sectionRef.current) {
        const rect = sectionRef.current.getBoundingClientRect();
        const sectionTop = rect.top + window.scrollY;
        const relativeScroll = window.scrollY - sectionTop + window.innerHeight;
        setScrollY(relativeScroll);
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const clientX = e.clientX;
    const clientY = e.clientY;
    requestAnimationFrame(() => {
      if (cardsContainerRef.current) {
        const rect = cardsContainerRef.current.getBoundingClientRect();
        const x = (clientX - rect.left - rect.width / 2) / (rect.width / 2);
        const y = (clientY - rect.top - rect.height / 2) / (rect.height / 2);
        setMousePosition({ x, y });
      }
    });
  }, []);

  const handleMouseLeave = useCallback(() => {
    setMousePosition({ x: 0, y: 0 });
  }, []);

  const get3DTransform = (index: number) => {
    const rotateX = -mousePosition.y * 8;
    const rotateY = mousePosition.x * 8;
    const parallaxOffset = scrollY * (0.02 + index * 0.01);
    return `
      perspective(1000px)
      translateY(${parallaxOffset}px)
      rotateX(${rotateX}deg)
      rotateY(${rotateY}deg)
    `;
  };

  const advantages = [
    {
      image: aboutExperience,
      value: "12+",
      label: t("about.advantage1"),
      gradient: "from-blue-500/20 to-indigo-600/20",
    },
    {
      image: aboutGlobal,
      value: "EU & CIS",
      label: t("about.advantage2"),
      gradient: "from-emerald-500/20 to-teal-600/20",
    },
    {
      image: aboutFintech,
      value: "Fintech",
      label: t("about.advantage3"),
      gradient: "from-violet-500/20 to-purple-600/20",
    },
    {
      image: aboutProjects,
      value: "100+",
      label: t("about.advantage4"),
      gradient: "from-cyan-500/20 to-blue-600/20",
    },
  ];

  return (
    <section ref={sectionRef} className="py-16 md:py-24 bg-background overflow-hidden">
      <div className="container">
        <div className="text-center mb-12">
          <span className="text-sm font-semibold uppercase tracking-wider text-primary mb-2 block">
            {t("about.label")}
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            {t("about.title")}
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed max-w-2xl mx-auto">
            {t("about.description")}
          </p>
        </div>

        <div 
          ref={cardsContainerRef}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8"
          style={{ perspective: "1000px" }}
        >
          {advantages.map((advantage, index) => (
            <div
              key={index}
              className="group relative rounded-2xl overflow-hidden border border-border/50 hover:border-primary/30 transition-all duration-500 hover:shadow-2xl hover:shadow-primary/10 cursor-pointer"
              style={{
                transform: get3DTransform(index),
                transformStyle: "preserve-3d",
                transition: "transform 0.3s ease-out, box-shadow 0.3s ease-out",
              }}
            >
              {/* Background Image */}
              <div className="absolute inset-0">
                <img
                  src={advantage.image}
                  alt={advantage.label}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  loading="lazy"
                />
                {/* Gradient Overlay */}
                <div className={`absolute inset-0 bg-gradient-to-t ${advantage.gradient} to-background/95 from-background/70`} />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-background/50 to-background/90" />
              </div>

              {/* Content */}
              <div className="relative p-6 md:p-8 text-center h-full flex flex-col justify-end min-h-[280px] md:min-h-[320px]">
                <div 
                  className="text-4xl md:text-5xl font-bold text-foreground mb-2 drop-shadow-lg transition-transform duration-300 group-hover:scale-110"
                  style={{
                    textShadow: "0 2px 10px rgba(0,0,0,0.3)",
                  }}
                >
                  {advantage.value}
                </div>
                <p className="text-sm md:text-base text-foreground/90 font-medium drop-shadow-md">
                  {advantage.label}
                </p>
              </div>

              {/* Shine effect on hover */}
              <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/5 to-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
            </div>
          ))}
        </div>

        {/* Decorative parallax elements */}
        <div
          className="absolute left-[10%] top-[20%] w-24 h-24 rounded-full bg-gradient-to-br from-primary/10 to-primary/5 blur-2xl pointer-events-none"
          style={{
            transform: `translateY(${scrollY * -0.05}px) translateX(${mousePosition.x * 15}px)`,
          }}
        />
        <div
          className="absolute right-[15%] bottom-[30%] w-32 h-32 rounded-full bg-gradient-to-br from-blue-500/10 to-indigo-500/5 blur-2xl pointer-events-none"
          style={{
            transform: `translateY(${scrollY * -0.08}px) translateX(${mousePosition.x * -20}px)`,
          }}
        />
      </div>
    </section>
  );
};

export default AboutCompanySection;
